import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "../CrewPilotNavRow";
import { usePermissions } from "../../hooks/usePermissions";
import { blockDemoSave } from "../../helpers/guestMode";
import {
  addProjectFeature,
  archiveProjectLocation,
  loadProjectStructure,
  formatTargetCompletion,
  updateProjectLocation,
} from "../../helpers/projectStructure";
import { safeUserErrorMessage } from "../../helpers/safeErrors";
import {
  WorkspaceAddButton,
  WorkspaceEmpty,
  WorkspaceHeaderMeta,
} from "../workspace/WorkspaceChrome";
import { WorkspaceNameSheet } from "../workspace/WorkspaceNameSheet";
import { ProjectFeatureWorkspace } from "./ProjectFeatureWorkspace";
import { ProjectPicturesScreen } from "./ProjectPicturesScreen";
import {
  loadProjectPictureSummaries,
  photoCountLabel,
} from "../../helpers/projectPictures";
import { countPendingPictures } from "../../helpers/pictureSync";
import { isTasksUnavailableError, loadProjectTasks } from "../../helpers/tasks/projectTasks";
import { countTasks, taskCountLabel } from "../../helpers/tasks/taskModel";
import { ProjectTasksScreen } from "../tasks/ProjectTasksScreen";
import "./projectDeskNav.css";

function featureSqft(feature) {
  return (feature.measurements || []).reduce(
    (sum, row) => sum + Number(row.area_sqft || 0),
    0,
  );
}

/**
 * Project location drill-down. Same row model as an Estimate location.
 * Project features are a name, so Add Feature is a focused name sheet
 * rather than the Estimate catalog chooser.
 */
export function ProjectLocationWorkspace({
  projectId,
  locationId,
  editable,
  writable = null,
  onBack,
  onPicturesChanged,
  locationRecord = null,
  pictureCount: pictureCountProp = null,
  projectName = "",
  onTasksChanged,
  tasks: tasksProp = null,
  initialFeatureId = "",
}) {
  const provided = Boolean(locationRecord);
  const { actor } = usePermissions();
  const [location, setLocation] = useState(locationRecord);
  const [loading, setLoading] = useState(!provided);
  const [error, setError] = useState("");
  const [showAddFeature, setShowAddFeature] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [activeFeatureId, setActiveFeatureId] = useState(initialFeatureId || "");
  const featureFromDeepLink = useRef(Boolean(initialFeatureId));
  const [showPictures, setShowPictures] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [tasksAvailable, setTasksAvailable] = useState(true);
  const [projectTasks, setProjectTasks] = useState(() => tasksProp || []);
  const [allLocations, setAllLocations] = useState([]);
  const [pictureCount, setPictureCount] = useState(() =>
    typeof pictureCountProp === "number"
      ? pictureCountProp
      : typeof locationRecord?.pictureCount === "number"
        ? locationRecord.pictureCount
        : null,
  );

  const reload = useCallback(async () => {
    if (provided) return;
    setLoading(true);
    setError("");
    try {
      const tree = await loadProjectStructure(actor, projectId);
      setAllLocations(tree);
      setLocation(tree.find((row) => row.project_location_id === locationId) || null);
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to load this location."));
    } finally {
      setLoading(false);
    }
  }, [actor, locationId, projectId, provided]);

  useEffect(() => {
    if (provided) {
      setLocation(locationRecord);
      setLoading(false);
      return;
    }
    reload();
  }, [provided, locationRecord, reload]);

  useEffect(() => {
    if (typeof pictureCountProp === "number") {
      setPictureCount(pictureCountProp);
      return undefined;
    }
    if (provided) {
      setPictureCount(
        typeof locationRecord?.pictureCount === "number" ? locationRecord.pictureCount : 0,
      );
      return undefined;
    }
    let cancelled = false;
    loadProjectPictureSummaries(actor, projectId)
      .then(async (summary) => {
        const local = await countPendingPictures(actor, projectId, locationId).catch(() => 0);
        if (!cancelled) setPictureCount((summary.byLocation.get(locationId) || 0) + local);
      })
      .catch(async () => {
        const local = await countPendingPictures(actor, projectId, locationId).catch(() => 0);
        if (!cancelled) setPictureCount(local);
      });
    return () => {
      cancelled = true;
    };
  }, [actor, locationId, locationRecord, pictureCountProp, projectId, provided]);

  const refreshLocationPictures = useCallback(async () => {
    const tasks = [];
    if (onPicturesChanged) tasks.push(Promise.resolve(onPicturesChanged()));
    if (typeof pictureCountProp === "number" || provided) {
      await Promise.all(tasks);
      return;
    }
    tasks.push(
      loadProjectPictureSummaries(actor, projectId)
        .then(async (summary) => {
          const local = await countPendingPictures(actor, projectId, locationId).catch(() => 0);
          setPictureCount((summary.byLocation.get(locationId) || 0) + local);
        })
        .catch(async () => {
          const local = await countPendingPictures(actor, projectId, locationId).catch(() => 0);
          setPictureCount(local);
        }),
    );
    await Promise.all(tasks);
  }, [actor, locationId, onPicturesChanged, pictureCountProp, projectId, provided]);

  // Tasks of this location are the SAME Project Task rows, filtered here.
  const refreshTasks = useCallback(async () => {
    if (tasksProp) return;
    try {
      const result = await loadProjectTasks(actor, projectId);
      setProjectTasks(result.tasks);
    } catch (error) {
      if (isTasksUnavailableError(error)) setTasksAvailable(false);
    }
  }, [actor, projectId, tasksProp]);

  useEffect(() => {
    refreshTasks();
  }, [refreshTasks]);

  const locationTaskList = (projectTasks || []).filter((task) => task.location_id === locationId);
  const features = location?.features || [];
  const locationName = location?.name || "Location";

  async function handleDelete() {
    if (!editable || !location) return;
    if (!window.confirm(`Delete location "${locationName}"?`)) return;
    if (blockDemoSave(actor)) return;
    try {
      await archiveProjectLocation(actor, location.project_location_id);
      onBack?.();
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to delete location."));
    }
  }

  if (showPictures && location) {
    return (
      <ProjectPicturesScreen
        projectId={projectId}
        projectName={locationName}
        editable={editable}
        writable={writable == null ? Boolean(editable) : writable}
        scope={{ locationId, locationName }}
        onLibraryChanged={refreshLocationPictures}
        onBack={() => {
          setShowPictures(false);
          refreshLocationPictures();
        }}
      />
    );
  }

  if (showTasks && location) {
    return (
      <ProjectTasksScreen
        projectId={projectId}
        projectName={projectName}
        locations={provided ? [location] : allLocations}
        scopeLocation={{ locationId, name: locationName }}
        writable={writable == null ? Boolean(editable) : writable}
        onChanged={(next) => {
          setProjectTasks(next);
          onTasksChanged?.(next);
        }}
        onBack={() => setShowTasks(false)}
      />
    );
  }

  if (activeFeatureId) {
    return (
      <ProjectFeatureWorkspace
        projectId={projectId}
        locationId={locationId}
        featureId={activeFeatureId}
        locationName={locationName}
        projectName={projectName}
        editable={editable}
        featureRecord={
          provided
            ? (location?.features || []).find((row) => row.project_feature_id === activeFeatureId) ||
              null
            : undefined
        }
        onBack={() => {
          if (featureFromDeepLink.current) {
            // Feature opened straight from Planning: Back leaves to where we came from.
            featureFromDeepLink.current = false;
            setActiveFeatureId("");
            onBack?.();
            return;
          }
          setActiveFeatureId("");
          reload();
        }}
      />
    );
  }

  return (
    <CrewPilotModuleShell
      title={locationName}
      sectionLabel="Location"
      onBack={onBack}
      desktopBackLabel={projectName || "Project"}
      crumbs={["Projects", projectName]}
    >
      {error && <div className="est-inline-error">{error}</div>}
      {loading && <WorkspaceEmpty>Loading location…</WorkspaceEmpty>}

      {!loading && location && (
        <>
          <WorkspaceHeaderMeta
            menuLabel="Location actions"
            menuItems={
              editable
                ? [
                    { label: "Edit Location", onClick: () => setShowRename(true) },
                    {
                      label: "Delete Location",
                      onClick: handleDelete,
                      destructive: true,
                    },
                  ]
                : []
            }
          >
            <span>
              {features.length} feature{features.length === 1 ? "" : "s"}
            </span>
            {location.target_completion_date ? (
              <span className="ws-target-meta">
                {" · Target "}
                {formatTargetCompletion(location.target_completion_date)}
              </span>
            ) : null}
          </WorkspaceHeaderMeta>

          <div className="ws-desk">
          <div className="ws-desk__main">
          <CrewPilotNavSectionHeader>Features</CrewPilotNavSectionHeader>

          {features.length === 0 ? (
            editable ? (
              <WorkspaceAddButton first onClick={() => setShowAddFeature(true)}>
                + Add First Feature
              </WorkspaceAddButton>
            ) : (
              <WorkspaceEmpty>No features yet.</WorkspaceEmpty>
            )
          ) : (
            <>
              <CrewPilotNavList>
                {features.map((feature) => {
                  const sqft = featureSqft(feature);
                  const target = formatTargetCompletion(feature.target_completion_date);
                  return (
                    <CrewPilotNavRow
                      key={feature.project_feature_id}
                      title={feature.name}
                      subtitle={[
                        sqft > 0 ? `${sqft.toFixed(0)} ft²` : null,
                        target ? `Target ${target}` : "No target date",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      onClick={() => setActiveFeatureId(feature.project_feature_id)}
                    />
                  );
                })}
              </CrewPilotNavList>
              {editable && (
                <WorkspaceAddButton onClick={() => setShowAddFeature(true)}>
                  + Add Feature
                </WorkspaceAddButton>
              )}
            </>
          )}

          </div>
          <div className="ws-desk__side">
          {tasksAvailable ? (
            <>
          <CrewPilotNavSectionHeader>Tasks</CrewPilotNavSectionHeader>
          <CrewPilotNavList>
            <CrewPilotNavRow
              title="Tasks"
              subtitle={
                countTasks(locationTaskList).total
                  ? taskCountLabel(locationTaskList)
                  : "No tasks in this location"
              }
              onClick={() => setShowTasks(true)}
            />
          </CrewPilotNavList>
            </>
          ) : null}

          <CrewPilotNavSectionHeader>Pictures</CrewPilotNavSectionHeader>
          <CrewPilotNavList>
            <CrewPilotNavRow
              title="Pictures"
              subtitle={pictureCount == null ? "…" : photoCountLabel(pictureCount)}
              onClick={() => setShowPictures(true)}
            />
          </CrewPilotNavList>
          </div>
          </div>
        </>
      )}

      {!loading && !location && <WorkspaceEmpty>This location is no longer available.</WorkspaceEmpty>}

      <div className="est-bottom-spacer" />

      {showAddFeature && (
        <WorkspaceNameSheet
          title="Add Feature"
          fieldLabel="Feature Name"
          placeholder="Shower"
          primaryLabel="Add Feature"
          withTargetCompletion
          onClose={() => setShowAddFeature(false)}
          onSubmit={async (name, options) => {
            if (blockDemoSave(actor)) {
              throw new Error("Demo Mode — Sign in to save your work.");
            }
            await addProjectFeature(actor, locationId, name, options);
            await reload();
          }}
        />
      )}

      {showRename && location && (
        <WorkspaceNameSheet
          title="Edit Location"
          fieldLabel="Location Name"
          placeholder="Bathroom"
          initialName={location.name}
          withTargetCompletion
          initialTargetCompletion={location.target_completion_date || ""}
          primaryLabel="Save Changes"
          onClose={() => setShowRename(false)}
          onSubmit={async (name, options) => {
            if (blockDemoSave(actor)) {
              throw new Error("Demo Mode — Sign in to save your work.");
            }
            await updateProjectLocation(actor, location.project_location_id, name, options);
            await reload();
          }}
        />
      )}
    </CrewPilotModuleShell>
  );
}
